import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ShopOutlined,
  GlobalOutlined,
  CheckCircleOutlined,
  EyeOutlined,
  RocketOutlined,
  KeyOutlined,
  GiftOutlined,
  GoldOutlined,
  PlusCircleOutlined,
  FileTextOutlined,
  EditOutlined,
  ArrowRightOutlined,
  CloseOutlined,
  CompassOutlined,
} from "@ant-design/icons";
import { useAuth } from "../../context/AuthContext";
import {
  GUIDED_JOURNEY_CONFIG,
  GUIDED_JOURNEY_STAGE,
  GUIDED_JOURNEY_TARGET,
  getGuidedJourneyBusinessTarget,
  resolveGuidedJourneyStage,
} from "../../config/guidedJourneyConfig";
import { GUIDED_JOURNEY_LABELS } from "../../labels/guidedJourneyLabels";
import {
  resolveUserSubscriptionAccess,
  SUBSCRIPTION_STATE,
} from "../../utils/subscriptionState";
import styles from "./GuidedJourney.module.css";

const GuidedJourneyContext = createContext(null);

const HUB_STEPS = [
  ["tourConfirm", null, false, true],
  ["hubDisplayName", GUIDED_JOURNEY_TARGET.HUB_DISPLAY_NAME],
  ["hubDomainName", GUIDED_JOURNEY_TARGET.HUB_DOMAIN_NAME],
  ["hubDomainCheck", GUIDED_JOURNEY_TARGET.HUB_DOMAIN_CHECK],
  ["hubDomainPreview", GUIDED_JOURNEY_TARGET.HUB_DOMAIN_PREVIEW],
  ["hubSubmit", GUIDED_JOURNEY_TARGET.HUB_SUBMIT, true],
];

const SUBSCRIPTION_STEPS = [
  ["tourConfirm", null, false, true],
  ["subscriptionActivation", GUIDED_JOURNEY_TARGET.SUBSCRIPTION_ACTIVATION],
  ["subscriptionTrial", GUIDED_JOURNEY_TARGET.SUBSCRIPTION_TRIAL],
  ["subscriptionPaid", GUIDED_JOURNEY_TARGET.SUBSCRIPTION_PAID, true],
];

function drawSpotlight(canvas, rect) {
  const context = canvas.getContext("2d");
  context.clearRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = "rgba(0, 0, 0, 0.66)";
  context.beginPath();
  context.rect(0, 0, canvas.width, canvas.height);
  const padding = GUIDED_JOURNEY_CONFIG.spotlightPadding;
  const x = Math.max(1, rect.left - padding);
  const y = Math.max(1, rect.top - padding);
  const width = Math.min(canvas.width - x - 1, rect.width + padding * 2);
  const height = Math.min(canvas.height - y - 1, rect.height + padding * 2);
  context.roundRect(x, y, width, height, GUIDED_JOURNEY_CONFIG.spotlightRadius);
  context.fill("evenodd");
  context.strokeStyle = getComputedStyle(document.body).getPropertyValue('--zh-brand-primary').trim() || '#1B6B82';
  context.lineWidth = 3;
  context.beginPath();
  context.roundRect(x, y, width, height, GUIDED_JOURNEY_CONFIG.spotlightRadius);
  context.stroke();
}

function getTooltipPosition(rect) {
  const margin = 12;
  const isMobile = window.innerWidth <= 600;
  const width = Math.min(
    GUIDED_JOURNEY_CONFIG.tooltipWidth,
    window.innerWidth - margin * 2,
  );
  const estimatedHeight = isMobile ? 250 : 220;
  const spaceBelow = window.innerHeight - rect.bottom - margin;
  const spaceAbove = rect.top - margin;
  const placement =
    spaceBelow >= estimatedHeight || spaceBelow >= spaceAbove ? "bottom" : "top";

  let top =
    placement === "bottom"
      ? rect.bottom + margin
      : rect.top - estimatedHeight - margin;
  top = Math.min(
    Math.max(margin, top),
    Math.max(margin, window.innerHeight - estimatedHeight - margin),
  );

  let left = rect.left + rect.width / 2 - width / 2;
  left = Math.min(Math.max(margin, left), window.innerWidth - width - margin);

  const targetCenterX = rect.left + rect.width / 2;
  const arrowLeft = Math.min(Math.max(20, targetCenterX - left), width - 20);

  return { left, top, placement, width, arrowLeft };
}

function storageKey(decodedToken) {
  const userId = decodedToken?.sub || decodedToken?.["cognito:username"] || "user";
  return `${GUIDED_JOURNEY_CONFIG.storagePrefix}:v${GUIDED_JOURNEY_CONFIG.version}:${userId}`;
}

function readProgress(key) {
  try {
    return JSON.parse(localStorage.getItem(key) || "null");
  } catch {
    return null;
  }
}

function buildStep(labelKey, targetId, waitsForAction = false, isConfirm = false) {
  return {
    ...GUIDED_JOURNEY_LABELS.steps[labelKey],
    labelKey,
    targetId,
    waitsForAction,
    isConfirm,
  };
}

export function GuidedJourneyProvider({ children }) {
  const { token, decodeToken } = useAuth();
  const decodedToken = useMemo(() => decodeToken(token), [decodeToken, token]);
  const access = useMemo(
    () => resolveUserSubscriptionAccess(decodedToken),
    [decodedToken],
  );
  const key = useMemo(() => storageKey(decodedToken), [decodedToken]);
  const navigate = useNavigate();
  const location = useLocation();
  const [stage, setStage] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [draftBizId, setDraftBizId] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({
    left: 12,
    top: 12,
    width: GUIDED_JOURNEY_CONFIG.tooltipWidth,
    arrowLeft: 30,
  });
  const [placement, setPlacement] = useState("bottom");
  const [targetUnavailable, setTargetUnavailable] = useState(false);
  const [stepBlocked, setStepBlocked] = useState(false);
  const canvasRef = useRef(null);
  const previousAccessActiveRef = useRef(access.active);

  const STEP_ICON = useMemo(() => ({
    hubDisplayName: ShopOutlined,
    hubDomainName: GlobalOutlined,
    hubDomainCheck: CheckCircleOutlined,
    hubDomainPreview: EyeOutlined,
    hubSubmit: RocketOutlined,
    subscriptionActivation: KeyOutlined,
    subscriptionTrial: GiftOutlined,
    subscriptionPaid: GoldOutlined,
    createBusiness: PlusCircleOutlined,
    draftTab: FileTextOutlined,
    draftBusiness: EditOutlined,
  }), []);

  const persistStage = useCallback((nextStage, extra = {}) => {
    localStorage.setItem(key, JSON.stringify({ stage: nextStage, ...extra }));
    setStage(nextStage);
  }, [key]);

  useEffect(() => {
    if (!token) return;
    const progress = readProgress(key);
    const resolvedStage = resolveGuidedJourneyStage(access.state, progress?.stage);
    if (resolvedStage) {
      setStage(resolvedStage);
      setStepIndex(0);
      setDraftBizId(progress?.draftBizId || null);
    }
  }, [access.state, key, token]);

  useEffect(() => {
    const wasActive = previousAccessActiveRef.current;
    previousAccessActiveRef.current = access.active;
    if (!wasActive && access.active) {
      setStepIndex(0);
      persistStage(GUIDED_JOURNEY_STAGE.CREATE_BUSINESS);
      navigate("/hub", { replace: true });
    }
  }, [access.active, navigate, persistStage]);

  useEffect(() => {
    if (
      (stage === GUIDED_JOURNEY_STAGE.HUB_SETUP ||
        stage === GUIDED_JOURNEY_STAGE.WAITING_FOR_HUB) &&
      access.state === SUBSCRIPTION_STATE.ACTIVATION_REQUIRED
    ) {
      setStepIndex(0);
      persistStage(GUIDED_JOURNEY_STAGE.SUBSCRIPTION);
      navigate("/subscription", { replace: true });
    }
    if (
      (stage === GUIDED_JOURNEY_STAGE.SUBSCRIPTION ||
        stage === GUIDED_JOURNEY_STAGE.WAITING_FOR_SUBSCRIPTION) &&
      access.active
    ) {
      setStepIndex(0);
      persistStage(GUIDED_JOURNEY_STAGE.CREATE_BUSINESS);
      navigate("/hub", { replace: true });
    }
  }, [access.active, access.state, navigate, persistStage, stage]);

  useEffect(() => {
    if (stage === GUIDED_JOURNEY_STAGE.SUBSCRIPTION && location.pathname !== "/subscription") {
      navigate("/subscription", { replace: true });
    } else if (
      (stage === GUIDED_JOURNEY_STAGE.CREATE_BUSINESS ||
        stage === GUIDED_JOURNEY_STAGE.FIRST_DRAFT) &&
      location.pathname !== "/hub"
    ) {
      navigate("/hub", { replace: true });
    }
  }, [location.pathname, navigate, stage]);

  const steps = useMemo(() => {
    if (stage === GUIDED_JOURNEY_STAGE.HUB_SETUP) {
      return HUB_STEPS.map((step) => buildStep(...step));
    }
    if (stage === GUIDED_JOURNEY_STAGE.SUBSCRIPTION) {
      return SUBSCRIPTION_STEPS.map((step) => buildStep(...step));
    }
    if (stage === GUIDED_JOURNEY_STAGE.CREATE_BUSINESS) {
      return [buildStep("tourConfirm", GUIDED_JOURNEY_TARGET.CREATE_BUSINESS, false, true), buildStep("createBusiness", GUIDED_JOURNEY_TARGET.CREATE_BUSINESS, true)];
    }
    if (stage === GUIDED_JOURNEY_STAGE.FIRST_DRAFT) {
      return [
        buildStep("draftTab", GUIDED_JOURNEY_TARGET.DRAFT_TAB),
        buildStep("draftBusiness", getGuidedJourneyBusinessTarget(draftBizId)),
      ];
    }
    return [];
  }, [draftBizId, stage]);

  const currentStep = steps[stepIndex] || null;

  useEffect(() => {
    if (!currentStep || currentStep.isConfirm) return undefined;
    setStepBlocked(false);
    let cancelled = false;
    let timeoutId;
    const startedAt = Date.now();

    const positionTarget = () => {
      if (cancelled) return;
      const isCreateBusinessStep = currentStep.targetId === GUIDED_JOURNEY_TARGET.CREATE_BUSINESS;
      const target = isCreateBusinessStep
        ? document.querySelector(`[data-guide-target="${currentStep.targetId}"]`)
        : document.querySelector(`[data-guide-target="${currentStep.targetId}"]`) || document.getElementById(currentStep.targetId);
      const spotlightTarget = target?.closest('.ant-input-affix-wrapper') || target;
      if (!spotlightTarget || !canvasRef.current) {
        if (Date.now() - startedAt >= GUIDED_JOURNEY_CONFIG.targetWaitMs) {
          setTargetUnavailable(true);
        }
        timeoutId = window.setTimeout(positionTarget, GUIDED_JOURNEY_CONFIG.targetPollMs);
        return;
      }
      setTargetUnavailable(false);
      const isMobile = window.innerWidth <= 600;
      spotlightTarget.scrollIntoView({
        block: isMobile ? "center" : "nearest",
        inline: "nearest",
        behavior: "auto",
      });
      const measureAndPlace = () => {
        if (cancelled || !canvasRef.current) return;
        const canvas = canvasRef.current;
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
        const rect = spotlightTarget.getBoundingClientRect();
        drawSpotlight(canvas, rect);
        const pos = getTooltipPosition(rect);
        setTooltipPosition(pos);
        setPlacement(pos.placement);
      };
      // Wait for scroll/layout so rect matches the visible spotlight.
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(measureAndPlace);
      });
    };

    positionTarget();
    const onViewportChange = () => positionTarget();
    window.addEventListener("resize", onViewportChange);
    window.addEventListener("scroll", onViewportChange, true);
    let observer;
    if (stage === GUIDED_JOURNEY_STAGE.HUB_SETUP) {
      const formEl = document.querySelector('[data-guide-target]')?.closest('form, .hub-setup-form') || document.body;
      if (formEl) {
        observer = new MutationObserver(() => { if (!cancelled) positionTarget(); });
        observer.observe(formEl, { childList: true, subtree: true });
      }
    }
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      window.removeEventListener("resize", onViewportChange);
      window.removeEventListener("scroll", onViewportChange, true);
      if (observer) observer.disconnect();
    };
  }, [currentStep, stage]);

  const dismissJourney = useCallback(() => {
    setStage(null);
    setStepIndex(0);
    setDraftBizId(null);
  }, []);

  const finishJourney = useCallback(() => {
    localStorage.setItem(key, JSON.stringify({ stage: GUIDED_JOURNEY_STAGE.COMPLETE }));
    setStage(null);
    setStepIndex(0);
    setDraftBizId(null);
  }, [key]);

  const nextStep = useCallback(() => {
    if (!currentStep) return;
    if (currentStep.isConfirm) {
      setStepIndex((current) => current + 1);
      return;
    }
    if (
      currentStep.targetId === GUIDED_JOURNEY_TARGET.HUB_DOMAIN_CHECK
    ) {
      const domainInput = document.querySelector(`[data-guide-target="${GUIDED_JOURNEY_TARGET.HUB_DOMAIN_NAME}"]`);
      const hasDomainValue = domainInput?.value?.trim()?.length > 0;
      if (hasDomainValue && document.getElementById(currentStep.targetId)?.dataset.domainChecked !== "true") {
        setStepBlocked(true);
        return;
      }
    }
    if (currentStep.waitsForAction) {
      if (stage === GUIDED_JOURNEY_STAGE.HUB_SETUP) {
        persistStage(GUIDED_JOURNEY_STAGE.WAITING_FOR_HUB);
      } else if (stage === GUIDED_JOURNEY_STAGE.SUBSCRIPTION) {
        persistStage(GUIDED_JOURNEY_STAGE.WAITING_FOR_SUBSCRIPTION);
      } else if (stage === GUIDED_JOURNEY_STAGE.CREATE_BUSINESS) {
        persistStage(GUIDED_JOURNEY_STAGE.WAITING_FOR_BUSINESS);
      }
      return;
    }
    if (stepIndex < steps.length - 1) {
      setStepIndex((current) => current + 1);
    } else {
      finishJourney();
    }
  }, [currentStep, finishJourney, persistStage, stage, stepIndex, steps.length]);

  const restartJourney = useCallback(() => {
    const progress = readProgress(key);
    console.log("[TEMP restartJourney] key:", key, "progress stage:", progress?.stage);
    if (progress?.stage === GUIDED_JOURNEY_STAGE.COMPLETE) {
      console.log("[TEMP restartJourney] COMPLETE — skipping restart");
      return;
    }
    setStepIndex(0);
    setDraftBizId(null);
    if (access.state === SUBSCRIPTION_STATE.HUB_SETUP_REQUIRED) {
      persistStage(GUIDED_JOURNEY_STAGE.HUB_SETUP);
    } else if (!access.active) {
      persistStage(GUIDED_JOURNEY_STAGE.SUBSCRIPTION);
      navigate("/subscription");
    } else {
      persistStage(GUIDED_JOURNEY_STAGE.CREATE_BUSINESS);
      navigate("/hub");
    }
  }, [access.active, access.state, key, navigate, persistStage]);

  const markCreateBusinessStarted = useCallback(() => {
    persistStage(GUIDED_JOURNEY_STAGE.WAITING_FOR_BUSINESS);
  }, [persistStage]);

  const showFirstDraft = useCallback((bizId) => {
    setDraftBizId(bizId);
    setStepIndex(0);
    localStorage.setItem(key, JSON.stringify({
      stage: GUIDED_JOURNEY_STAGE.FIRST_DRAFT,
      draftBizId: bizId,
    }));
    setStage(GUIDED_JOURNEY_STAGE.FIRST_DRAFT);
  }, [key]);

  const completeBusinessJourney = useCallback(() => {
    finishJourney();
  }, [finishJourney]);

  const value = useMemo(() => ({
    restartJourney,
    markCreateBusinessStarted,
    showFirstDraft,
    completeBusinessJourney,
    isCreateBusinessStep: stage === GUIDED_JOURNEY_STAGE.CREATE_BUSINESS ||
      stage === GUIDED_JOURNEY_STAGE.WAITING_FOR_BUSINESS,
  }), [
    completeBusinessJourney,
    markCreateBusinessStarted,
    restartJourney,
    showFirstDraft,
    stage,
  ]);

  const realSteps = useMemo(() => steps.filter((s) => !s.isConfirm), [steps]);

  return (
    <GuidedJourneyContext.Provider value={value}>
      {children}
      {currentStep && currentStep.isConfirm && createPortal((
        <>
          <div className={styles.confirmBackdrop} />
          <section className={styles.tooltipConfirm}>
          <div className={styles.confirmIconInline}><CompassOutlined /></div>
          <h2 className={styles.title}>
            {stage === GUIDED_JOURNEY_STAGE.SUBSCRIPTION
              ? GUIDED_JOURNEY_LABELS.tourConfirmTitleSub
              : GUIDED_JOURNEY_LABELS.tourConfirmTitle}
          </h2>
          <p className={styles.description}>
            {stage === GUIDED_JOURNEY_STAGE.SUBSCRIPTION
              ? GUIDED_JOURNEY_LABELS.tourConfirmDescSub
              : GUIDED_JOURNEY_LABELS.tourConfirmDesc}
          </p>
          <div className={styles.actions}>
            <button type="button" className={styles.secondaryButton} onClick={dismissJourney}>
              {GUIDED_JOURNEY_LABELS.tourConfirmSkip}
            </button>
            <button type="button" className={styles.primaryButton} onClick={nextStep}>
              {stage === GUIDED_JOURNEY_STAGE.SUBSCRIPTION
                ? GUIDED_JOURNEY_LABELS.tourConfirmBtnSub
                : GUIDED_JOURNEY_LABELS.tourConfirmBtn}
            </button>
          </div>
          </section>
          </>
      ), document.body)}
      {currentStep && !currentStep.isConfirm && createPortal((
        <>
          <canvas ref={canvasRef} className={styles.canvas} aria-hidden="true" />
          <section
            className={styles.tooltip}
            style={{
              left: tooltipPosition.left,
              top: tooltipPosition.top,
              width: tooltipPosition.width,
              "--guide-arrow-left": `${tooltipPosition.arrowLeft ?? 30}px`,
            }}
            data-placement={placement}
            role="dialog"
            aria-modal="false"
            aria-labelledby="guided-journey-title"
            aria-describedby="guided-journey-description"
          >
            <div className={styles.header}>
              <span className={styles.counter}>{GUIDED_JOURNEY_LABELS.step} {realSteps.indexOf(currentStep) + 1} / {realSteps.length}</span>
              <button type="button" className={styles.closeButton} onClick={dismissJourney} aria-label={GUIDED_JOURNEY_LABELS.close}>
                <CloseOutlined />
              </button>
            </div>
            <h2 id="guided-journey-title" className={styles.title}>{currentStep.title}</h2>
            <div className={styles.descriptionRow}>
              <p id="guided-journey-description" className={styles.description}>
                {targetUnavailable
                  ? GUIDED_JOURNEY_LABELS.targetUnavailable
                  : stepBlocked
                    ? GUIDED_JOURNEY_LABELS.domainMustBeAvailable
                  : currentStep.description}
              </p>
              <span className={styles.iconWrapper}>
                {React.createElement(STEP_ICON[currentStep.labelKey] || ShopOutlined)}
              </span>
            </div>
<div className={styles.confirmActions}>
              <button type="button" className={styles.secondaryButton} onClick={dismissJourney}>
                {GUIDED_JOURNEY_LABELS.skip}
              </button>
              <button type="button" className={styles.primaryButton} onClick={nextStep}>
                {stepIndex === steps.length - 1
                  ? GUIDED_JOURNEY_LABELS.finish
                  : currentStep.waitsForAction
                    ? GUIDED_JOURNEY_LABELS.waiting
                    : <>{GUIDED_JOURNEY_LABELS.next} <ArrowRightOutlined /></>}
              </button>
            </div>
            <div className={styles.dots} aria-hidden="true">
              {realSteps.map((step, index) => (
                <span
                  key={step.targetId}
                  className={`${styles.dot} ${step === currentStep ? styles.dotActive : ""}`}
                />
              ))}
            </div>
          </section>
        </>
      ), document.body)}
    </GuidedJourneyContext.Provider>
  );
}

export function useGuidedJourney() {
  const context = useContext(GuidedJourneyContext);
  if (!context) {
    throw new Error("useGuidedJourney must be used inside GuidedJourneyProvider");
  }
  return context;
}
